import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/payments/subscription/plans/`);

        if (!response.ok) {
          throw new Error("Failed to fetch plans");
        }
        const data = await response.json();
        console.log(data);
        setPlans(data);
      } catch (err) {
        console.error("Error fetching plans:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const getMonthlyPrice = (plan) => {
    if (!plans) return 0;
    const monthlyPlan = plans.find((planName) => planName.name === plan.name && planName.billing_interval === "monthly");
    return monthlyPlan ? parseFloat(monthlyPlan.price) : 0;
  };

  const getYearlyPrice = (plan) => {
    if (!plans) return 0;
    const yearlyPlan = plans.find((planName) => planName.name === plan.name && planName.billing_interval === "yearly");
    return yearlyPlan ? parseFloat(yearlyPlan.price) : 0;
  };

  const getMonthlyPlan = (plan) => {
    if (!plans) return null;
    return plans.find((planName) => planName.name === plan.name && planName.billing_interval === "monthly");
  };

  const getYearlyPlan = (plan) => {
    if (!plans) return null;
    return plans.find((planName) => planName.name === plan.name && planName.billing_interval === "yearly");
  };

  const getFeatures = (plan) => {
    const features = plan.description.split(",");
    return features.map((feature) => feature.trim());
  };

  const handleGetStarted = (plan) => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const checkout = urlParams.get("checkout");
    const planId = plan.id;

    console.log(plan);

    if (parseInt(plan.price) === 0) {
      window.location.href = "/login";
      return;
    }

    if (checkout === "true" && token) {
      // Navigate to checkout page with token, plan id, and similar plans in the query params
      const params = new URLSearchParams({
        checkout: "true",
        token: token,
        plan_id: planId,
        monthly_id: getMonthlyPlan(plan).id,
        yearly_id: getYearlyPlan(plan).id,
        billing: billingCycle,
      });

      window.location.href = `/checkout/?${params.toString()}`;
    } else {
      console.log("redirecting to login page");
      window.location.href = "/login";
    }
  };

  return (
    <section id="pricing" className="py-12 md:py-16 relative">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#222425] mb-6 tracking-tight">
            Simple, Transparent Pricing
          </h2>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-12">
            <div className="bg-gray-100 rounded-xl p-1 inline-flex">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-8 py-3 rounded-lg font-medium text-sm transition-all duration-300 ${
                  billingCycle === "monthly"
                    ? "bg-gradient-to-br from-[#4F8EF7] to-[#2c72d9] text-white shadow-md"
                    : "text-gray-600 hover:text-[#222425]"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`px-8 py-3 rounded-lg font-medium text-sm transition-all duration-300 ${
                  billingCycle === "yearly"
                    ? "bg-gradient-to-br from-[#4F8EF7] to-[#2c72d9] text-white shadow-md"
                    : "text-gray-600 hover:text-[#222425]"
                }`}
              >
                Yearly
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-cta mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading plans...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md inline-block">
              Error loading plans: {error}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        {!loading && !error && plans && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            {plans
              .filter((plan) => {
                // Filter plans based on billing cycle
                const billingInterval = billingCycle === "yearly" ? "yearly" : "monthly";
                return plan.billing_interval === billingInterval && plan.is_active;
              })
              .map((plan, index) => {
                const isYearly = billingCycle === "yearly";
                const features = getFeatures(plan);
                const planPrice = parseFloat(plan.price);
                const monthlyPrice = getMonthlyPrice(plan);
                const displayPrice = isYearly 
                  ? Math.round(planPrice / 12) 
                  : planPrice;
                const yearlyTotal = isYearly ? planPrice : null;
                const savings = isYearly && monthlyPrice > 0 ? monthlyPrice - displayPrice : 0;

              return (
                <div
                  key={index}
                  className={`bg-white rounded-3xl p-10 flex flex-col transition-all duration-300 ${
                    plan.highlight
                      ? "ring-2 ring-[#00DA76] shadow-2xl transform scale-105"
                      : "border border-gray-100 hover:border-[#00DA76]/30 hover:shadow-xl"
                  }`}
                >
                  {/* Plan Header */}
                  <div className="mb-8">
                    <h3 className="text-3xl font-bold text-[#222425] mb-2">
                      {plan.name}
                    </h3>
                    <p className="text-gray-600 text-base font-light">
                      {plan.subtitle}
                    </p>

                    {/* Price */}
                    <div className="mt-6">
                      {isYearly && monthlyPrice >= 0 ? (
                        <div className="flex items-baseline gap-3">
                          {monthlyPrice > 0 && (
                            <span className="text-2xl font-bold text-gray-300 line-through">
                              ${monthlyPrice}
                            </span>
                          )}
                          <span className="text-5xl font-bold text-[#222425]">
                            ${displayPrice}
                          </span>
                          <span className="text-gray-500 text-base font-light">
                            /month
                          </span>
                        </div>
                      ) : (
                        <>
                          <span className="text-5xl font-bold text-[#222425]">
                            ${displayPrice}
                          </span>
                          {displayPrice > 0 && (
                            <span className="text-gray-500 text-base font-light ml-2">
                              /month
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {billingCycle === "monthly" && (
                      <p className="text-gray-500 text-xs mt-1">
                        Billed monthly (${displayPrice}/month)
                      </p>
                    )}
                    {billingCycle === "yearly" && (
                      <p className="text-gray-500 text-xs mt-1">
                        Billed annually (${planPrice}/year)
                      </p>
                    )}
                  </div>

                  {/* Features List */}
                  <div className="flex-1 mb-8">
                    <ul className="space-y-3 md:space-y-4">
                      {features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-start gap-3"
                        >
                          <svg
                            className="w-5 h-5 md:w-6 md:h-6 text-brand-dark flex-shrink-0 mt-0.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-gray-700 text-sm md:text-base">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleGetStarted(plan)}
                    className="w-full py-2.5 text-center bg-[#222425] hover:bg-gradient-to-br hover:from-[#4F8EF7] hover:to-[#2c72d9] text-white rounded-xl font-semibold text-sm shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
                  >
                    <span>Get Started</span>
                    <svg
                      className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingPage;
